import { Children } from 'react';
import clsx from 'clsx';
import { MonetaryAmount, RecurringMonetaryAmount } from '../../../generated/graphql';
import { TableTitle, TableTitleProps } from './TableTitle';
import { TableRowsContainer, TableRowsContainerProps } from './TableRowsContainer';
import { AmountSumTableAcceptableTableChildren } from './types';
import { SumTableSumContainer, SumTableSumContainerProps } from './SumTableSumContainer';
import { TableContainer, TableContainerProps } from './TableContainer';
import { useDisplayAmount } from '../../../DataPoint';
export interface AmountSumTableSection {
  title: string;
  children?: AmountSumTableAcceptableTableChildren | AmountSumTableAcceptableTableChildren[] | boolean;
}

export interface AmountSumTableProps
  extends TableTitleProps,
    TableRowsContainerProps<AmountSumTableAcceptableTableChildren>,
    Omit<SumTableSumContainerProps, 'sumDisplay'>,
    Omit<TableContainerProps, 'children' | 'hide'> {
  className?: string;
  hideOnEmpty?: boolean;
  sum?: MonetaryAmount | RecurringMonetaryAmount | null;
  header?: JSX.Element;
  sections?: AmountSumTableSection[];
}

export function AmountSumTable({
  sum,
  title,
  children,
  sumTitle,
  isLoading,
  className,
  sumAppendix,
  hideOnEmpty,
  header,
  sections,
}: AmountSumTableProps) {
  const { displayAmount } = useDisplayAmount();
  const visibleSections = sections
    ?.map((section) => ({
      ...section,
      children: Children.toArray(section.children),
    }))
    .filter((section) => section.children.length > 0);
  const rows = sections ? visibleSections : children;
  const hide = hideOnEmpty && !isLoading && (!rows || (Array.isArray(rows) && rows.length === 0));

  return (
    <TableContainer hide={hide} isLoading={isLoading} className={clsx(className, 'AmountSumTable')}>
      <TableTitle title={title} className="AmountSumTable-TableTitle" />
      {header}
      <TableRowsContainer className={clsx('AmountSumTable-TableRowsContainer', sections && 'space-y-4')}>
        {sections
          ? visibleSections?.map((section) => (
              <section key={section.title} className="AmountSumTable-Section">
                <h3 className="AmountSumTable-SectionTitle mb-2 px-4 text-sm font-bold leading-normal tracking-[0.286px] text-darkMode-gray-medium">
                  {section.title}
                </h3>
                <div className="AmountSumTable-SectionRows">{section.children}</div>
              </section>
            ))
          : children}
      </TableRowsContainer>
      <SumTableSumContainer
        sumTitle={sumTitle}
        sumAppendix={sumAppendix}
        className="AmountSumTable-SumTableSumContainer"
        sumDisplay={displayAmount(sum, {
          nullishIsDash: true,
          withoutDollarSign: true,
        })}
      />
    </TableContainer>
  );
}
